import { Dispatch, SetStateAction } from "react";
import { FormControlLabel, Switch, useTheme, useMediaQuery } from "@mui/material";
import { DirectionsCar, EvStation, LocalParking } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../../app/helpers";
import { RootState } from "../../../../app/rootReducer";
import {
  AdvancedBookingSettingsGroup,
  AdvancedBookingSettingsSetters,
  CheckinPolicyGroup
} from "../CompanyCard.types";
import LabeledSlider from "../../../../components/Common/LabeledSlider/LabeledSlider";
import { CheckinAndMaxAdvancedBooking, MetaData } from "../../../Login/typings/login.types";
import { updateCheckInSettings } from "../../functions/company-card.component.functions";
import { BookingType } from "../../../Booking-Form/typings/booking-inputs";
import {
  AdvancedBookingPerTypeRowIcon,
  AdvancedPerTypeAccordion
} from "./CompanyCard-Setting-AdvancedPerTypeAccordion.partial";
import { generateCheckinRowContents } from "../functions/generateCheckinPerTypeAccordionRowContents.function";
import { ReactComponent as AvatarIcon } from "../../../Booking-Form/assets/avatar.svg";
import { ReactComponent as ConferenceIcon } from "../../../Booking-Form/assets/conference.svg";
import {
  CONFERENCE_ZONE_TYPE_ID,
  ELECTRIC_CHARGING_PLACE_TYPE_ID,
  PARKING_PLACE_TYPE_ID,
  VEHICLE_PLACE_TYPE_ID,
  WORKPLACE_PLACE_TYPE_ID
} from "../../../Booking-Form/typings/place-and-zone-type-ids.constant";
import { getBookingTypeEnabled } from "./company-card-booking-type-enabling.config";

type P = {
  checkinEnabledGrp: CheckinPolicyGroup;
  setCheckinEnabledGrp: Dispatch<SetStateAction<CheckinPolicyGroup>>;
  newSetting: AdvancedBookingSettingsGroup;
  setNewSetting: AdvancedBookingSettingsSetters;
  newValues: MetaData;
  setNewValues: Dispatch<SetStateAction<MetaData>>;
};

export function CompanyCardCheckinPolicyPerType({
  checkinEnabledGrp,
  setCheckinEnabledGrp,
  newSetting: { wpSetting, parkPlSetting, eChargePlSetting, confZnSetting, vehicleSetting },
  setNewSetting: {
    setWpSetting,
    setParkPlSetting,
    setEChargePlSetting,
    setConfZnSetting,
    setVehicleSetting
  },
  newValues,
  setNewValues
}: Readonly<P>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    userInformation: { placeTypes, zoneTypes }
  } = useSelector((state: RootState) => state.login);

  const workplaceBookingEnabled = getBookingTypeEnabled(newValues, "workplaceBookingEnabled");
  const parkingplaceBookingEnabled = getBookingTypeEnabled(newValues, "parkingplaceBookingEnabled");
  const electricchargingplaceBookingEnabled = getBookingTypeEnabled(
    newValues,
    "electricchargingplaceBookingEnabled"
  );
  const conferencezoneBookingEnabled = getBookingTypeEnabled(
    newValues,
    "conferencezoneBookingEnabled"
  );
  const vehicleBookingEnabled = getBookingTypeEnabled(newValues, "vehicleBookingEnabled");

  const sliderWidth = isMobile ? "85%" : "90%";
  const sliderMarginLeft = isMobile ? "40px" : "52px";
  const childFormLabelMarginLeft = isMobile ? 0 : "30px";

  const rowContentsPerType = (setting: CheckinAndMaxAdvancedBooking, checkinEnabled: boolean) => {
    if (!checkinEnabled) return <></>;
    return <span>{generateCheckinRowContents(setting, t)}</span>;
  };

  return (
    <>
      {/* checkin policy switch - workplace */}
      {workplaceBookingEnabled && placeTypes.some(t => t.id === WORKPLACE_PLACE_TYPE_ID) && (
        <AdvancedPerTypeAccordion
          title={t("Workplace")}
          contents={rowContentsPerType(wpSetting, checkinEnabledGrp.workplace)}
          typeIcon={
            <AdvancedBookingPerTypeRowIcon icon={<AvatarIcon width={"18px"} height={"18px"} />} />
          }
        >
          <FormControlLabel
            control={
              <Switch
                data-testid={"checkInPolicySwitch_workplace"}
                checked={checkinEnabledGrp.workplace}
                onChange={(e, c) => {
                  /* istanbul ignore next */
                  setCheckinEnabledGrp(prevState => ({
                    ...prevState,
                    workplace: c
                  }));
                }}
                name="checkinPolicy"
                slotProps={{ input: { "aria-label": "secondary checkbox" } }}
              />
            }
            label={t("checkinPolicyWorkplace")}
          />

          {/* checkin policy switch - period minutes - for workplace */}
          {checkinEnabledGrp.workplace && (
            <>
              {/* period minutes for workplace */}
              <LabeledSlider
                width={sliderWidth}
                marginLeft={sliderMarginLeft}
                paddingRight="16px"
                data-testid="checkinPolicy-label-slider-workplace"
                value={wpSetting.checkinBufferTime || -1}
                label={t("CheckinPeriodMinutes")}
                onChange={(e, v) => {
                  /* istanbul ignore next */
                  setWpSetting(prev => ({ ...prev, checkinBufferTime: v as number }));
                  /* istanbul ignore next */
                  setNewValues?.(prevState =>
                    /* istanbul ignore next */
                    updateCheckInSettings(prevState, BookingType.WORKPLACE, {
                      ...wpSetting,
                      checkinBufferTime: v as number
                    })
                  );
                }}
                min={15}
                max={60}
                step={15}
                ariaLabel={"Checkin Buffer Time"}
              />

              {/* geo checking activate for workplace */}
              <FormControlLabel
                sx={{ marginLeft: childFormLabelMarginLeft }}
                control={
                  <Switch
                    data-testid={"geoChecking_Switch_workplace"}
                    checked={wpSetting.geoLocationRequired || false}
                    onChange={(e, c) => {
                      /* istanbul ignore next */
                      setWpSetting(prev => ({
                        ...prev,
                        geoLocationRequired: c,
                        checkinWhenOpeningDoor: c ? prev.checkinWhenOpeningDoor : false
                      }));
                      /* istanbul ignore next */
                      if (setNewValues) {
                        setNewValues(prevState =>
                          /* istanbul ignore next */
                          updateCheckInSettings(prevState, BookingType.WORKPLACE, {
                            ...wpSetting,
                            geoLocationRequired: c,
                            // check if unchecking the checkbox, set checkinWhenOpeningDoor to false as well
                            checkinWhenOpeningDoor: c ? wpSetting.checkinWhenOpeningDoor : false
                          })
                        );
                      } else {
                        setWpSetting(prev => ({ ...prev, bookingType: BookingType.WORKPLACE }));
                      }
                    }}
                    name="geoCheckingActivate"
                    slotProps={{ input: { "aria-label": "secondary checkbox" } }}
                  />
                }
                label={t("geoCheckingActivate")}
              />

              {/* check in via opening doors for workplace */}
              {wpSetting.geoLocationRequired && (
                <FormControlLabel
                  sx={{ marginLeft: childFormLabelMarginLeft }}
                  control={
                    <Switch
                      data-testid={"checkInViaOpeningDoors_Switch_workplace"}
                      checked={wpSetting.checkinWhenOpeningDoor || false}
                      onChange={(e, c) => {
                        /* istanbul ignore next */
                        setWpSetting(prev => ({ ...prev, checkinWhenOpeningDoor: c }));
                        /* istanbul ignore next */
                        setNewValues?.(prevState =>
                          /* istanbul ignore next */
                          updateCheckInSettings(prevState, BookingType.WORKPLACE, {
                            ...wpSetting,
                            checkinWhenOpeningDoor: c
                          })
                        );
                      }}
                      name="checkInViaOpeningDoorsActivate"
                      slotProps={{ input: { "aria-label": "secondary checkbox" } }}
                    />
                  }
                  label={t("checkInViaOpeningDoors")}
                />
              )}
            </>
          )}
        </AdvancedPerTypeAccordion>
      )}

      {/* checkin policy switch - parking place */}
      {parkingplaceBookingEnabled && placeTypes.some(t => t.id === PARKING_PLACE_TYPE_ID) && (
        <AdvancedPerTypeAccordion
          title={t("Parking")}
          contents={rowContentsPerType(parkPlSetting, checkinEnabledGrp.parkingplace)}
          typeIcon={
            <AdvancedBookingPerTypeRowIcon icon={<LocalParking width={"18px"} height={"18px"} />} />
          }
        >
          <FormControlLabel
            control={
              <Switch
                data-testid={"checkInPolicySwitch_parkingplace"}
                checked={checkinEnabledGrp.parkingplace}
                onChange={(e, c) => {
                  /* istanbul ignore next */
                  setCheckinEnabledGrp(prevState => ({
                    ...prevState,
                    parkingplace: c
                  }));
                }}
                name="checkinPolicy"
                slotProps={{ input: { "aria-label": "secondary checkbox" } }}
              />
            }
            label={t("checkinPolicyParkingplace")}
          />

          {/* checkin policy switch - period minutes - for parking place */}
          {checkinEnabledGrp.parkingplace && (
            <>
              <LabeledSlider
                width={sliderWidth}
                marginLeft={sliderMarginLeft}
                paddingRight="16px"
                data-testid="checkinPolicy-label-slider-parkingplace"
                value={parkPlSetting.checkinBufferTime || -1}
                label={t("CheckinPeriodMinutes")}
                onChange={(e, v) => {
                  /* istanbul ignore next */
                  setParkPlSetting(prev => ({ ...prev, checkinBufferTime: v as number }));
                  /* istanbul ignore next */
                  setNewValues?.(prevState =>
                    /* istanbul ignore next */
                    updateCheckInSettings(prevState, BookingType.PARKINGPLACE, {
                      ...parkPlSetting,
                      checkinBufferTime: v as number
                    })
                  );
                }}
                min={15}
                max={60}
                step={15}
                ariaLabel={"Checkin Buffer Time"}
              />

              {/* geo checking activate for parking place */}
              <FormControlLabel
                sx={{ marginLeft: childFormLabelMarginLeft }}
                control={
                  <Switch
                    data-testid={"geoChecking_Switch_parkingplace"}
                    checked={parkPlSetting.geoLocationRequired || false}
                    onChange={(e, c) => {
                      /* istanbul ignore next */
                      setParkPlSetting(prev => ({
                        ...prev,
                        geoLocationRequired: c,
                        checkinWhenOpeningDoor: c ? prev.checkinWhenOpeningDoor : false
                      }));
                      /* istanbul ignore next */
                      setNewValues?.(prevState =>
                        /* istanbul ignore next */
                        updateCheckInSettings(prevState, BookingType.PARKINGPLACE, {
                          ...parkPlSetting,
                          geoLocationRequired: c,
                          // check if unchecking the checkbox, set checkinWhenOpeningDoor to false as well
                          checkinWhenOpeningDoor: c ? parkPlSetting.checkinWhenOpeningDoor : false
                        })
                      );
                    }}
                    name="geoCheckingActivate"
                    slotProps={{ input: { "aria-label": "secondary checkbox" } }}
                  />
                }
                label={t("geoCheckingActivate")}
              />

              {/* check in via opening doors for parking place */}
              {parkPlSetting.geoLocationRequired && (
                <FormControlLabel
                  sx={{ marginLeft: childFormLabelMarginLeft }}
                  control={
                    <Switch
                      data-testid={"checkInViaOpeningDoors_Switch_parkingplace"}
                      checked={parkPlSetting.checkinWhenOpeningDoor || false}
                      onChange={(e, c) => {
                        /* istanbul ignore next */
                        setParkPlSetting(prev => ({ ...prev, checkinWhenOpeningDoor: c }));
                        /* istanbul ignore next */
                        setNewValues?.(prevState =>
                          /* istanbul ignore next */
                          updateCheckInSettings(prevState, BookingType.PARKINGPLACE, {
                            ...parkPlSetting,
                            checkinWhenOpeningDoor: c
                          })
                        );
                      }}
                      name="checkInViaOpeningDoorsActivate"
                      slotProps={{ input: { "aria-label": "secondary checkbox" } }}
                    />
                  }
                  label={t("checkInViaOpeningDoors")}
                />
              )}
            </>
          )}
        </AdvancedPerTypeAccordion>
      )}

      {/* checkin policy switch - e-charging parking place */}
      {electricchargingplaceBookingEnabled &&
        placeTypes.some(t => t.id === ELECTRIC_CHARGING_PLACE_TYPE_ID) && (
          <AdvancedPerTypeAccordion
            title={t("Electric Charging")}
            contents={rowContentsPerType(eChargePlSetting, checkinEnabledGrp.electricchargingplace)}
            typeIcon={
              <AdvancedBookingPerTypeRowIcon icon={<EvStation width={"18px"} height={"18px"} />} />
            }
          >
            <FormControlLabel
              control={
                <Switch
                  data-testid={"checkInPolicySwitch_electricChargingPlace"}
                  checked={checkinEnabledGrp.electricchargingplace}
                  onChange={(e, c) => {
                    /* istanbul ignore next */
                    setCheckinEnabledGrp(prevState => ({
                      ...prevState,
                      electricchargingplace: c
                    }));
                  }}
                  name="checkinPolicy"
                  slotProps={{ input: { "aria-label": "secondary checkbox" } }}
                />
              }
              label={t("checkinPolicyElectricChargingPlace")}
            />

            {/* checkin policy switch - period minutes - for electric charging place */}
            {checkinEnabledGrp.electricchargingplace && (
              <>
                <LabeledSlider
                  width={sliderWidth}
                  marginLeft={sliderMarginLeft}
                  paddingRight="16px"
                  data-testid="checkinPolicy-label-slider-electricchargingplace"
                  value={eChargePlSetting.checkinBufferTime || -1}
                  label={t("CheckinPeriodMinutes")}
                  onChange={(e, v) => {
                    /* istanbul ignore next */
                    setEChargePlSetting(prev => ({ ...prev, checkinBufferTime: v as number }));
                    /* istanbul ignore next */
                    setNewValues?.(prevState =>
                      /* istanbul ignore next */
                      updateCheckInSettings(prevState, BookingType.ELECTRICCHARGINGSTATIONPLACE, {
                        ...eChargePlSetting,
                        checkinBufferTime: v as number
                      })
                    );
                  }}
                  min={15}
                  max={60}
                  step={15}
                  ariaLabel={"Checkin Buffer Time"}
                />

                {/* geo checking activate for electric charging place */}
                <FormControlLabel
                  sx={{ marginLeft: childFormLabelMarginLeft }}
                  control={
                    <Switch
                      data-testid={"geoChecking_Switch_electricchargingplace"}
                      checked={eChargePlSetting.geoLocationRequired || false}
                      onChange={(e, c) => {
                        /* istanbul ignore next */
                        setEChargePlSetting(prev => ({
                          ...prev,
                          geoLocationRequired: c,
                          checkinWhenOpeningDoor: c ? prev.checkinWhenOpeningDoor : false
                        }));
                        /* istanbul ignore next */
                        setNewValues?.(prevState =>
                          /* istanbul ignore next */
                          updateCheckInSettings(
                            prevState,
                            BookingType.ELECTRICCHARGINGSTATIONPLACE,
                            {
                              ...eChargePlSetting,
                              geoLocationRequired: c,
                              // check if unchecking the checkbox, set checkinWhenOpeningDoor to false as well
                              checkinWhenOpeningDoor: c
                                ? eChargePlSetting.checkinWhenOpeningDoor
                                : false
                            }
                          )
                        );
                      }}
                      name="geoCheckingActivate"
                      slotProps={{ input: { "aria-label": "secondary checkbox" } }}
                    />
                  }
                  label={t("geoCheckingActivate")}
                />

                {/* check in via opening doors for electric charging place */}
                {eChargePlSetting.geoLocationRequired && (
                  <FormControlLabel
                    sx={{ marginLeft: childFormLabelMarginLeft }}
                    control={
                      <Switch
                        data-testid={"checkInViaOpeningDoors_Switch_electricchargingplace"}
                        checked={eChargePlSetting.checkinWhenOpeningDoor || false}
                        onChange={(e, c) => {
                          /* istanbul ignore next */
                          setEChargePlSetting(prev => ({ ...prev, checkinWhenOpeningDoor: c }));
                          /* istanbul ignore next */
                          setNewValues?.(prevState =>
                            /* istanbul ignore next */
                            updateCheckInSettings(
                              prevState,
                              BookingType.ELECTRICCHARGINGSTATIONPLACE,
                              {
                                ...eChargePlSetting,
                                checkinWhenOpeningDoor: c
                              }
                            )
                          );
                        }}
                        name="checkInViaOpeningDoorsActivate"
                        slotProps={{ input: { "aria-label": "secondary checkbox" } }}
                      />
                    }
                    label={t("checkInViaOpeningDoors")}
                  />
                )}
              </>
            )}
          </AdvancedPerTypeAccordion>
        )}

      {/* checkin policy switch - vehicle */}
      {vehicleBookingEnabled && placeTypes.some(t => t.id === VEHICLE_PLACE_TYPE_ID) && (
        <AdvancedPerTypeAccordion
          title={t("_bookingTypeVehicle")}
          contents={rowContentsPerType(vehicleSetting, checkinEnabledGrp.vehicle)}
          typeIcon={
            <AdvancedBookingPerTypeRowIcon
              icon={<DirectionsCar width={"18px"} height={"18px"} />}
            />
          }
        >
          <FormControlLabel
            control={
              <Switch
                data-testid={"checkInPolicySwitch_vehicle"}
                checked={checkinEnabledGrp.vehicle}
                onChange={(e, c) => {
                  /* istanbul ignore next */
                  setCheckinEnabledGrp(prevState => ({
                    ...prevState,
                    vehicle: c
                  }));
                }}
                name="checkinPolicy"
                slotProps={{ input: { "aria-label": "secondary checkbox" } }}
              />
            }
            label={t("checkinPolicyVehicle")}
          />

          {/* checkin policy switch - period minutes - for vehicle */}
          {checkinEnabledGrp.vehicle && (
            <>
              <LabeledSlider
                width={sliderWidth}
                marginLeft={sliderMarginLeft}
                paddingRight="16px"
                data-testid="checkinPolicy-label-slider-vehicle"
                value={vehicleSetting.checkinBufferTime || -1}
                label={t("CheckinPeriodMinutes")}
                onChange={(e, v) => {
                  /* istanbul ignore next */
                  setVehicleSetting(prev => ({ ...prev, checkinBufferTime: v as number }));
                  /* istanbul ignore next */
                  setNewValues?.(prevState =>
                    /* istanbul ignore next */
                    updateCheckInSettings(prevState, BookingType.VEHICLE, {
                      ...vehicleSetting,
                      checkinBufferTime: v as number
                    })
                  );
                }}
                min={15}
                max={60}
                step={15}
                ariaLabel={"Checkin Buffer Time"}
              />

              {/* geo checking activate for vehicle */}
              <FormControlLabel
                sx={{ marginLeft: childFormLabelMarginLeft }}
                control={
                  <Switch
                    data-testid={"geoChecking_Switch_vehicle"}
                    checked={vehicleSetting.geoLocationRequired || false}
                    onChange={(e, c) => {
                      /* istanbul ignore next */
                      setVehicleSetting(prev => ({
                        ...prev,
                        geoLocationRequired: c,
                        checkinWhenOpeningDoor: c ? prev.checkinWhenOpeningDoor : false
                      }));
                      /* istanbul ignore next */
                      setNewValues?.(prevState =>
                        /* istanbul ignore next */
                        updateCheckInSettings(prevState, BookingType.VEHICLE, {
                          ...vehicleSetting,
                          geoLocationRequired: c,
                          // check if unchecking the checkbox, set checkinWhenOpeningDoor to false as well
                          checkinWhenOpeningDoor: c ? vehicleSetting.checkinWhenOpeningDoor : false
                        })
                      );
                    }}
                    name="geoCheckingActivate"
                    slotProps={{ input: { "aria-label": "secondary checkbox" } }}
                  />
                }
                label={t("geoCheckingActivate")}
              />

              {/* check in via opening doors for vehicle */}
              {vehicleSetting.geoLocationRequired && (
                <FormControlLabel
                  sx={{ marginLeft: childFormLabelMarginLeft }}
                  control={
                    <Switch
                      data-testid={"checkInViaOpeningDoors_Switch_vehicle"}
                      checked={vehicleSetting.checkinWhenOpeningDoor || false}
                      onChange={(e, c) => {
                        /* istanbul ignore next */
                        setVehicleSetting(prev => ({ ...prev, checkinWhenOpeningDoor: c }));
                        /* istanbul ignore next */
                        setNewValues?.(prevState =>
                          /* istanbul ignore next */
                          updateCheckInSettings(prevState, BookingType.VEHICLE, {
                            ...vehicleSetting,
                            checkinWhenOpeningDoor: c
                          })
                        );
                      }}
                      name="checkInViaOpeningDoorsActivate"
                      slotProps={{ input: { "aria-label": "secondary checkbox" } }}
                    />
                  }
                  label={t("checkInViaOpeningDoors")}
                />
              )}
            </>
          )}
        </AdvancedPerTypeAccordion>
      )}

      {/* checkin policy switch - conference zone */}
      {conferencezoneBookingEnabled && zoneTypes.some(t => t.id === CONFERENCE_ZONE_TYPE_ID) && (
        <AdvancedPerTypeAccordion
          title={t("_bookTypeMeetingRoom")}
          contents={rowContentsPerType(confZnSetting, checkinEnabledGrp.conferencezone)}
          typeIcon={
            <AdvancedBookingPerTypeRowIcon
              icon={<ConferenceIcon width={"18px"} height={"18px"} />}
            />
          }
        >
          <FormControlLabel
            control={
              <Switch
                data-testid={"checkInPolicySwitch_conferenceZone"}
                checked={checkinEnabledGrp.conferencezone}
                onChange={(e, c) => {
                  /* istanbul ignore next */
                  setCheckinEnabledGrp(prevState => ({
                    ...prevState,
                    conferencezone: c
                  }));
                }}
                name="checkinPolicy"
                slotProps={{ input: { "aria-label": "secondary checkbox" } }}
              />
            }
            label={t("checkinPolicyConferenceZone")}
          />

          {/* checkin policy switch - period minutes - for conference zone */}
          {checkinEnabledGrp.conferencezone && (
            <>
              <LabeledSlider
                width={sliderWidth}
                marginLeft={sliderMarginLeft}
                paddingRight="16px"
                data-testid="checkinPolicy-label-slider-conferencezone"
                value={confZnSetting.checkinBufferTime || -1}
                label={t("CheckinPeriodMinutes")}
                onChange={(e, v) => {
                  /* istanbul ignore next */
                  setConfZnSetting(prev => ({ ...prev, checkinBufferTime: v as number }));
                  /* istanbul ignore next */
                  setNewValues?.(prevState =>
                    /* istanbul ignore next */
                    updateCheckInSettings(prevState, BookingType.CONFERENCEZONE, {
                      ...confZnSetting,
                      checkinBufferTime: v as number
                    })
                  );
                }}
                min={15}
                max={60}
                step={15}
                ariaLabel={"Checkin Buffer Time"}
              />

              {/* geo checking activate for conference zone */}
              <FormControlLabel
                sx={{ marginLeft: childFormLabelMarginLeft }}
                control={
                  <Switch
                    data-testid={"geoChecking_Switch_conferencezone"}
                    checked={confZnSetting.geoLocationRequired || false}
                    onChange={(e, c) => {
                      /* istanbul ignore next */
                      setConfZnSetting(prev => ({
                        ...prev,
                        geoLocationRequired: c,
                        checkinWhenOpeningDoor: c ? prev.checkinWhenOpeningDoor : false
                      }));
                      /* istanbul ignore next */
                      setNewValues?.(prevState =>
                        /* istanbul ignore next */
                        updateCheckInSettings(prevState, BookingType.CONFERENCEZONE, {
                          ...confZnSetting,
                          geoLocationRequired: c,
                          // check if unchecking the checkbox, set checkinWhenOpeningDoor to false as well
                          checkinWhenOpeningDoor: c ? confZnSetting.checkinWhenOpeningDoor : false
                        })
                      );
                    }}
                    name="geoCheckingActivate"
                    slotProps={{ input: { "aria-label": "secondary checkbox" } }}
                  />
                }
                label={t("geoCheckingActivate")}
              />

              {/* check in via opening doors for conference zone */}
              {confZnSetting.geoLocationRequired && (
                <FormControlLabel
                  sx={{ marginLeft: childFormLabelMarginLeft }}
                  control={
                    <Switch
                      data-testid={"checkInViaOpeningDoors_Switch_conferencezone"}
                      checked={confZnSetting.checkinWhenOpeningDoor || false}
                      onChange={(e, c) => {
                        /* istanbul ignore next */
                        setConfZnSetting(prev => ({ ...prev, checkinWhenOpeningDoor: c }));
                        /* istanbul ignore next */
                        setNewValues?.(prevState =>
                          /* istanbul ignore next */
                          updateCheckInSettings(prevState, BookingType.CONFERENCEZONE, {
                            ...confZnSetting,
                            checkinWhenOpeningDoor: c
                          })
                        );
                      }}
                      name="checkInViaOpeningDoorsActivate"
                      slotProps={{ input: { "aria-label": "secondary checkbox" } }}
                    />
                  }
                  label={t("checkInViaOpeningDoors")}
                />
              )}
            </>
          )}
        </AdvancedPerTypeAccordion>
      )}
    </>
  );
}
